import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import { buildBackendUrl } from '../../../config/API';

const AdaptiveSessionVideo = ({ videos }) => {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [quality, setQuality] = useState('auto');
  const [levels, setLevels] = useState([]);

  const adaptiveVideo = useMemo(
    () => videos.find((video) => video?.hls_url),
    [videos]
  );
  const fallbackVideos = useMemo(
    () => videos.filter((video) => video?.video_path),
    [videos]
  );
  const hlsUrl = adaptiveVideo?.hls_url ? buildBackendUrl(adaptiveVideo.hls_url) : '';

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return undefined;

    setQuality('auto');
    setLevels([]);

    let disposed = false;
    const attachAdaptiveStream = async () => {
      if (hlsUrl) {
        const { default: Hls } = await import('hls.js');
        if (disposed) return;
        if (Hls.isSupported()) {
          const hls = new Hls({ startLevel: -1, capLevelToPlayerSize: true });
          hlsRef.current = hls;
          hls.on(Hls.Events.MANIFEST_PARSED, (_, data) => {
            setLevels(data.levels.map((level, index) => ({
              index,
              height: level.height,
              bitrate: level.bitrate,
            })));
          });
          hls.on(Hls.Events.ERROR, (_, data) => {
            if (data.fatal && fallbackVideos[0]?.video_path) {
              hls.destroy();
              hlsRef.current = null;
              videoElement.src = buildBackendUrl(fallbackVideos[0].video_path);
            }
          });
          hls.loadSource(hlsUrl);
          hls.attachMedia(videoElement);
          return;
        }

        if (videoElement.canPlayType('application/vnd.apple.mpegurl')) {
          videoElement.src = hlsUrl;
          return;
        }
      }

      if (fallbackVideos[0]?.video_path) {
        videoElement.src = buildBackendUrl(fallbackVideos[0].video_path);
      }
    };
    attachAdaptiveStream();

    return () => {
      disposed = true;
      hlsRef.current?.destroy();
      hlsRef.current = null;
      videoElement.removeAttribute('src');
      videoElement.load();
    };
  }, [fallbackVideos, hlsUrl]);

  const handleQualityChange = (event) => {
    const nextQuality = event.target.value;
    setQuality(nextQuality);

    if (hlsRef.current) {
      hlsRef.current.currentLevel = nextQuality === 'auto' ? -1 : Number(nextQuality);
      return;
    }

    const fallback = fallbackVideos.find((video) => video.session_part === nextQuality);
    if (fallback?.video_path && videoRef.current) {
      const currentTime = videoRef.current.currentTime;
      videoRef.current.src = buildBackendUrl(fallback.video_path);
      videoRef.current.currentTime = currentTime;
      videoRef.current.play().catch(() => {});
    }
  };

  const qualityOptions = levels.length
    ? levels.map((level) => ({ value: String(level.index), label: `${level.height}p` }))
    : fallbackVideos.map((video) => ({ value: video.session_part, label: video.session_part }));

  return (
    <Box sx={{ width: 'min(760px, 82vw)' }}>
      <Box sx={{ width: '100%', aspectRatio: '16 / 9', bgcolor: '#000' }}>
        <video
          ref={videoRef}
          data-testid="adaptive-session-video"
          controls
          playsInline
          crossOrigin="anonymous"
          style={{ display: 'block', width: '100%', height: '100%', objectFit: 'contain' }}
        />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1.5 }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="recording-quality-label">Quality</InputLabel>
          <Select
            labelId="recording-quality-label"
            value={quality}
            label="Quality"
            onChange={handleQualityChange}
          >
            {hlsUrl && <MenuItem value="auto">Auto</MenuItem>}
            {qualityOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="caption" color="text.secondary">
          {hlsUrl ? 'Adaptive streaming' : 'MP4 playback'}
        </Typography>
      </Box>
    </Box>
  );
};

AdaptiveSessionVideo.propTypes = {
  videos: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default AdaptiveSessionVideo;
